import React, { useState, useEffect } from 'react';
import { X, Mail, Shield, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { rolesService } from '../services/rolesService';
import { UserRole } from '../types/roles';
import { Collaborator } from '../types/collaborators';
import authService from '../services/authService';

interface InviteCollaboratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  collaborator: Collaborator;
  onSuccess: () => void;
}

const InviteCollaboratorModal: React.FC<InviteCollaboratorModalProps> = ({
  isOpen,
  onClose,
  collaborator,
  onSuccess
}) => {
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadRoles();
      setSelectedRole('');
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  const loadRoles = async () => {
    try {
      setLoadingRoles(true);
      const allRoles = await rolesService.getAllRoles();
      const collaboratorRoles = allRoles.filter(
        r => r.name === 'colaborador_programador' || r.name === 'colaborador_comercial'
      );
      setRoles(collaboratorRoles);
    } catch (err) {
      console.error('Error loading roles:', err);
      setError('No se pudieron cargar los roles disponibles');
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedRole) {
      setError('Por favor selecciona un rol para el colaborador');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const session = authService.getCurrentSession();
      if (!session || !session.user) {
        throw new Error('No se pudo obtener el usuario actual');
      }
      const currentUser = session.user;

      let invitationCreated = false;

      try {
        await rolesService.createInvitation(
          collaborator.email,
          collaborator.id,
          selectedRole,
          currentUser.id
        );
        invitationCreated = true;

        await rolesService.grantCRMAccess(
          collaborator.id,
          selectedRole,
          currentUser.id
        );
      } catch (err: any) {
        if (invitationCreated) {
          console.error('Error granting CRM access after invitation was created:', err);
          setError(
            'La invitación fue enviada pero hubo un error configurando los permisos. ' +
            'Por favor edita el usuario manualmente para asignar el rol correcto.'
          );
        } else {
          throw err;
        }
        return;
      }

      setSuccess(
        `Invitación enviada exitosamente a ${collaborator.email}.\n` +
        `El colaborador recibirá un email con instrucciones para activar su cuenta.`
      );

      setTimeout(() => {
        onSuccess();
        onClose();
      }, 3000);
    } catch (err: any) {
      console.error('Error sending invitation:', err);
      setError(err.message || 'Error al enviar la invitación. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Invitar Colaborador al CRM
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40"
            disabled={loading && !success}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleInvite} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-red-900">Error</h4>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-green-900">Éxito</h4>
                  <p className="text-sm text-green-700 mt-1 whitespace-pre-line">{success}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-blue-900">Colaborador</h4>
                <p className="text-sm text-blue-700 mt-1">
                  <strong>{collaborator.name}</strong>
                  <br />
                  {collaborator.email}
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rol en el CRM *
            </label>
            {loadingRoles ? (
              <div className="flex items-center justify-center py-4">
                <Loader className="w-5 h-5 animate-spin text-blue-600" />
              </div>
            ) : (
              <select
                required
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Seleccionar rol</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.display_name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {selectedRole && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-gray-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Permisos del rol</h4>
                  <p className="text-xs text-gray-600 mt-1">
                    {roles.find((r) => r.id === selectedRole)?.description}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-yellow-900">Importante</h4>
                <ul className="text-xs text-yellow-700 mt-1 space-y-1">
                  <li>• Se enviará un email con un enlace de activación</li>
                  <li>• El colaborador definirá su propia contraseña</li>
                  <li>• El enlace expira en 7 días</li>
                  <li>• Podrás revocar el acceso en cualquier momento</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !selectedRole}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading && <Loader className="w-4 h-4 animate-spin" />}
              <span>{loading ? 'Enviando...' : 'Enviar Invitación'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteCollaboratorModal;
